import {certificateElementType} from "~/enums/certificateElementType";
import GridCellCertificateElement from "./gridCellCertificateElement";
import DynamicModeCertificateElement from "./dynamicCertificateElement";
import AdaptiveLayoutSection from "./adaptiveLayoutSection";
import {HorizontalAlignment} from "~/enums/certificates/alignment";
import CertificateDynamicInput from "./certificateDynamicInput";
import Vue from "vue"

export default class GridCertificateElement extends DynamicModeCertificateElement{
  type: certificateElementType = certificateElementType.Grid;
  column_count: number;
  row_count: number;

  horizontal_alignment: number;
  outline_thickness: number;
  selectedGridCells: Array<GridCellCertificateElement> = []

  vertical_cell_margin: number;
  horizontal_cell_margin: number;

  padding_top: number;
  padding_bottom: number;
  padding_left: number;
  padding_right: number;

  parent: AdaptiveLayoutSection | GridCellCertificateElement

  get canMerge(): boolean{
    const fullCells = this.selectedGridCells.filter(gc=> gc.isFull)
    return fullCells.length <= 1
  }

  get isConfiguredToRepeatableInput() : boolean{
    return !!this.repeatable_input_id
  }

  get certificateDynamicInput() : CertificateDynamicInput{
    let dynamicInputParent;
    if (this.isNestedGrid){
      dynamicInputParent = this.parentGrid.certificateDynamicInput
    }else{
      dynamicInputParent = this.certificate
    }
    if (this.repeatable_input_id === null) {
      return dynamicInputParent.certificateDynamicInputs[0];
    } else {
      return dynamicInputParent.certificateDynamicInputs.filter(x => x.client_id === this.repeatable_input_id)[0] || dynamicInputParent.certificateDynamicInputs[0];
    }
  }

  instanceCount(repeatable_index: number = null) : number{
    return this.liveData(repeatable_index).length
  }

  liveData(repeatable_index: number = null) {
    return this.certificate.getMappedDataValue(this.repeatable_input_id, repeatable_index)
  }

  get nestingLevel() : number{
    if (!this.isNestedGrid){
      return 0
    }else{
      //
      return this.parentGrid.nestingLevel + 1
    }
  }

  get parentGrid() : GridCertificateElement{
    if (!this.isNestedGrid){
      return
    }else{
      //The parent is the grid cell then the parent of the grid cell is the grid
      return (this.parent.parent as GridCertificateElement);
    }
  }

  get isNestedGrid(){
    return !(this.parent instanceof AdaptiveLayoutSection)
  }

  get leftBoundX(){
    return this.parent.leftBoundX
  }
  get gridMatrix(){
    let matrix = []
    let gridCellIndex = 0
    for (let i = 0; i < this.row_count; i++ ){
      matrix.push([])
      for (let j = 0; j < this.column_count; j++ ){
        let gridCell = this.certificateElementsArray[gridCellIndex++]
        matrix[i].push(gridCell)
      }
    }
    return matrix
  }

  selectGridCells(e, gridCell){
    if (!e.shiftKey) {
      this.selectedGridCells = []
    }
    if (this.selectedGridCells.includes(gridCell)) {
      const index = this.selectedGridCells.indexOf(gridCell);
      this.selectedGridCells.splice(index, 1);
    } else {
      this.selectedGridCells.push(gridCell);
    }
    const bounds = this.selectedCellsBounds
    for (let j = bounds.startingRow; j <= bounds.lastRow; j++) {
      let currentRow = this.gridMatrix[j]
      for (let i = bounds.startingColumn; i <= bounds.lastColumn; i++) {
        let currentCell = currentRow[i]
        if (!this.selectedGridCells.includes(currentCell)) {
          this.selectedGridCells.push(currentCell);
        }
      }
    }
  }

  get selectedCellsBounds() : {startingColumn: number, startingRow: number, lastRow: number, lastColumn: number} {
    let startingColumn, startingRow, lastColumn, lastRow;
    this.selectedGridCells.forEach(cell => {
      if (startingColumn === undefined || cell.columnIndex < startingColumn) {
        startingColumn = cell.columnIndex
      }
      if (startingRow === undefined || cell.rowIndex < startingRow) {
        startingRow = cell.rowIndex
      }
      const lastCellColumn = cell.parentCell.columnIndex +  cell.parentCell.column_span - 1
      if (lastColumn === undefined || lastCellColumn > lastColumn) {
        lastColumn = lastCellColumn
      }
      const lastCellRow = cell.parentCell.rowIndex + cell.parentCell.row_span - 1
      if (lastRow === undefined || lastCellRow > lastRow) {
        lastRow = lastCellRow
      }
    })
    return {startingColumn: startingColumn, startingRow: startingRow, lastRow: lastRow, lastColumn: lastColumn}
  }

  mergeCells() : void{
    const bounds = this.selectedCellsBounds
    const rowSpan = bounds.lastRow - bounds.startingRow + 1;
    const colSpan = bounds.lastColumn - bounds.startingColumn + 1;
    const gridMatrix = this.gridMatrix
    const parentCell = gridMatrix[bounds.startingRow][bounds.startingColumn]
    for (let j = bounds.startingRow; j <= bounds.lastRow; j++) {
      let currentRow = gridMatrix[j]
      for (let i = bounds.startingColumn; i <= bounds.lastColumn; i++) {
        let currentCell = currentRow[i]
        if (currentCell === parentCell){
          currentCell.column_span = colSpan
          currentCell.row_span = rowSpan
        }else{
          if (colSpan > 1){
            currentCell.column_span = 0
          }
          if(rowSpan > 1){
            currentCell.row_span = 0
          }
          currentCell.parent_cell_client_id = parentCell.client_id
        }
      }
    }
  }

  get initialGridWidth(): number{
    return this.maxWidth
  }

  get maxWidth(): number {
    if (this.parent instanceof AdaptiveLayoutSection) {
      return this.parent.dimensions.width - this.parent.totalHorizontalMargin
    } else {
      return (this.parent as GridCellCertificateElement).cellWidth
    }
  }

  get rightBoundX(): number {
    return this.width + this.leftBoundX
  }

  get isFull(): boolean{
    return  (this.certificateElementsArray as Array<GridCellCertificateElement>).filter(x=> x.isFull || x.hasBeenVerticallyMergedInto || x.hasBeenVerticallyMergedInto).length ===  this.certificateElementsArray.length
  }

  addRow(){
    if (this.type === certificateElementType.Grid){
      this.row_count ++
    }
    for(let i = 0; i < this.column_count; i++){
      this.appendGridCell(false)
    }
  }

  get x(): number {
    switch (this.horizontal_alignment) {
      case HorizontalAlignment.Left:
        return 0;
      case HorizontalAlignment.Middle:
        return (this.maxWidth - this.width) / 2;
      case HorizontalAlignment.Right:
        return this.maxWidth - this.width;
    }
  }

  insertColumn(newColumnPosition) {
    const newCellsJSON = []
    const gridCells = this.certificateElementsArray;
    for (let rowIndex = 0; rowIndex < this.row_count; rowIndex++) {
      const currentRow = this.row(rowIndex);
      const leftHorizontallyAdjacentCell = currentRow[newColumnPosition-1]
      const rightHorizontallyAdjacentCell = currentRow[newColumnPosition]
      let isHorizontallyMergedInto = false
      let isVerticallyMergedInto = false;
      let mergingCell;
      if(leftHorizontallyAdjacentCell?.isHorizontallyMergedIntoOtherCells ||
        (rightHorizontallyAdjacentCell?.hasBeenHorizontallyMergedInto && leftHorizontallyAdjacentCell?.hasBeenHorizontallyMergedInto)){
        if (rightHorizontallyAdjacentCell && rightHorizontallyAdjacentCell.hasBeenHorizontallyMergedInto) {
          mergingCell = rightHorizontallyAdjacentCell.parentCell
        } else {
          mergingCell = leftHorizontallyAdjacentCell.parentCell
        }
        isVerticallyMergedInto = mergingCell.isVerticallyMergedIntoOtherCells
        if(currentRow.includes(mergingCell)){
          mergingCell.column_span++
        }
        isHorizontallyMergedInto = true
      }
      //Add rowindex to include all cells that were newly added
      const indexOfNewCell =  (rowIndex * this.column_count) + newColumnPosition + rowIndex;
      let json : {type: certificateElementType, parent_cell_client_id: string, sort_order: number, column_span?: number, row_span?: number} = {type: certificateElementType.GridCell, sort_order: indexOfNewCell, parent_cell_client_id: mergingCell?.client_id}
      if (isHorizontallyMergedInto){
        json.column_span = 0
      }
      if (isVerticallyMergedInto){
        json.row_span = 0
      }
      newCellsJSON.push(json)
    }
    this.column_count++;
    newCellsJSON.forEach(json => {
      const newGridCell = this.appendGridCell(false, json.sort_order, json);
      this.elementHandler.updateElementPositions(this, newGridCell, newGridCell.sort_order);
    })
    //Select Last added cell
    this.select()


  }


  insertRow(newRowPosition) {
    const newCellsJSON = []
    const gridCells = this.certificateElementsArray;
    const indexOfFirstCellToUpdate = newRowPosition * this.column_count;
    let newRowSortOrder = indexOfFirstCellToUpdate;
    const elementsToUpdate = gridCells.slice(indexOfFirstCellToUpdate, gridCells.length);

    for (let columnIndex = 0; columnIndex < this.column_count; columnIndex++) {
      let isHorizontallyMergedInto = false
      let isVerticallyMergedInto = false;
      const currentColumn = this.column(columnIndex)
      const topVerticallyAdjacentCell = currentColumn[newRowPosition-1]
      const bottomVerticallyAdjacentCell = currentColumn[newRowPosition]
      let mergingCell;
      if(topVerticallyAdjacentCell?.isVerticallyMergedIntoOtherCells ||
        (bottomVerticallyAdjacentCell?.hasBeenVerticallyMergedInto && bottomVerticallyAdjacentCell?.hasBeenVerticallyMergedInto)){
        if (bottomVerticallyAdjacentCell && bottomVerticallyAdjacentCell.hasBeenVerticallyMergedInto) {
          mergingCell = bottomVerticallyAdjacentCell.parentCell
        } else {
          mergingCell = bottomVerticallyAdjacentCell.parentCell
        }
        isHorizontallyMergedInto = mergingCell.isHorizontallyMergedIntoOtherCells
        if (currentColumn.includes(mergingCell)){
          mergingCell.row_span++
        }
        isVerticallyMergedInto = true
      }
      let json : {type: certificateElementType, parent_cell_client_id: string ,sort_order: number, row_span?: number, column_span?: number} = {type: certificateElementType.GridCell, sort_order: newRowSortOrder++, parent_cell_client_id: mergingCell?.client_id}
      if (isVerticallyMergedInto){
        json.row_span = 0
      }
      if (isHorizontallyMergedInto){
        json.column_span = 0
      }
      newCellsJSON.push(json)
    }
    this.row_count++;
    let gridCell
    newCellsJSON.forEach(json => {
      gridCell = this.appendGridCell(false, json.sort_order, json);
    })

    for (const element of elementsToUpdate) {
      element.sort_order += this.column_count;
    }

    Vue.nextTick(()=>{
      //Select grid
      this.select()
    })

  }

  removeColumn(columnPosition) {
    const cellsToDestroy = [];
    this.column(columnPosition).forEach(gridCell => {
      cellsToDestroy.push(gridCell.beforeDestroy())
    })
    cellsToDestroy.forEach(gridCell => gridCell.destroy())
    this.column_count--;
  }

  removeRow(rowPosition) {
    const cellsToDestroy = [];
    const gridCells = this.certificateElementsArray;
    const indexOfFirstCellToUpdate = (rowPosition + 1) * this.column_count;
    const elementsToUpdate = gridCells.slice(indexOfFirstCellToUpdate, gridCells.length);

    this.row(rowPosition).forEach(gridCell => {
      cellsToDestroy.push(gridCell.beforeDestroy())
    })
    this.row_count--;

    for (const element of elementsToUpdate) {
      element.sort_order -= this.column_count;
    }
    cellsToDestroy.forEach(gridCell => gridCell.destroy())
  }

  get firstEmptyCell(){
    return (this.certificateElementsArray as Array<GridCellCertificateElement>).filter(x=>  x.isEmpty && !x.hasBeenVerticallyMergedInto && !x.hasBeenHorizontallyMergedInto)[0]
  }

  row(rowIndex:number){
    return this.gridMatrix[rowIndex]
  }

  rowIsFullyMerged(rowIndex: number): boolean{
    return this.row(rowIndex).every(gridCell => gridCell?.row_span === 0 || gridCell?.column_span === 0)
  }

  verticalSplit(cellBeingSplit: GridCellCertificateElement): void{
    const startingRowIndex = cellBeingSplit.rowIndex + 1
    const lastRowIndex = cellBeingSplit.rowIndex + cellBeingSplit.row_span
    const column = this.column(cellBeingSplit.columnIndex)
    for(let i = startingRowIndex; i < lastRowIndex; i++  ){
      let splitCell = column[i]
      splitCell.row_span = 1
      if (splitCell.hasBeenHorizontallyMergedInto){
        const splitCellRowIndex = splitCell.rowIndex
        const splittingCellRow = this.row(splitCellRowIndex)
        const newlyMergedIntoCell = splittingCellRow[splitCell.columnIndex+cellBeingSplit.column_span-1]
        newlyMergedIntoCell.parent_cell_client_id = splitCell.client_id
        newlyMergedIntoCell.column_span = 0
        splitCell.column_span = cellBeingSplit.column_span
      }
      splitCell.parent_cell_client_id = null
      cellBeingSplit.row_span -= 1
    }
  }


  horizontalSplit(cellBeingSplit: GridCellCertificateElement): void{
    const startingColumnIndex = cellBeingSplit.columnIndex + 1
    const lastColumnIndex = cellBeingSplit.columnIndex + cellBeingSplit.column_span
    const row = this.row(cellBeingSplit.rowIndex)
    for(let i = startingColumnIndex; i < lastColumnIndex; i++  ){
      let splitCell = row[i]
      splitCell.column_span = 1
      if (splitCell.hasBeenVerticallyMergedInto){
        const splitCellColumnIndex = splitCell.columnIndex
        const splittingCellColumn = this.column(splitCellColumnIndex)
        const newlyMergedIntoCell = splittingCellColumn[splitCell.rowIndex+cellBeingSplit.row_span-1]
        newlyMergedIntoCell.parent_cell_client_id = splitCell.client_id
        newlyMergedIntoCell.row_spn = 0
        splitCell.row_span = cellBeingSplit.row_span
      }else{
        splitCell.parent_cell_client_id = null
      }
      cellBeingSplit.column_span -= 1
    }
  }

  get visibleRows(){
    const rows = []
    for(let i = 0; i < this.row_count; i++){
      if(!this.rowIsFullyMerged(i)){
        rows.push(this.row(i))
      }
    }
    return rows
  }

  get visibleColumns(){
    const columns = []
    for (let i = 0; i < this.column_count; i++){
      if(!this.columnIsFullyMerged(i)){
        columns.push(this.column(i))
      }
    }
    return columns
  }

  column(columnIndex: number) {
    const column = []
    for (let i = 0; i < this.row_count; i++) {
      column.push(this.gridMatrix[i][columnIndex])
    }
    return column
  }

  columnIsFullyMerged(columnIndex: number): boolean{
    return this.column(columnIndex).every(gridCell => gridCell.row_span === 0 || gridCell.column_span === 0)
  }

  /**
   * The inner width (i.e., that available to the cell's content) of a 1-column cell in this grid.
   * Note: This must be kept in sync with base_cell_width in grid_certificate_element.rb
   */
  get baseCellWidth() {
    const totalPadding = this.padding_right + this.padding_left;
    // +2 for left/right edges of row, -1 to calculate the number of _gaps_ between cells
    const totalMargin  = (2 + (this.column_count - 1)) * this.horizontal_cell_margin;
    // +1 to account for parent's border in addition to borders of cells in a row
    const totalOutline = (this.outline_thickness * 2) * (this.column_count + 1);
    const totalCellWidth =  this.width - totalPadding - totalMargin - totalOutline;
    return (totalCellWidth / this.column_count);
  }

  initialize(root, parent: any = root, type, json: {} = {}): this {
    this.defineProperty("column_count", this.type === certificateElementType.Stack ? 1 : 2)
    this.defineProperty("row_count", 1)
    super.initialize(root, parent, type, json);
    if (this.certificateElementsArray.length === 0) {
      const startingRowCount = this.row_count
      let i = 0;
      while (i < this.column_count * startingRowCount){
        this.appendGridCell(false)
        i++;
      }
    }
    return this
  }

  appendGridCell(iterateRowCount = false, sortOrder: number = null, json ={type: certificateElementType.GridCell, sort_order: sortOrder}) : GridCellCertificateElement {
    sortOrder = sortOrder === 0 ? 0 : sortOrder || this.certificateElementsArray.length;
    const gridCell  = (this.createEmbeddedObject.certificate_elements(this, {type: certificateElementType.GridCell, sort_order: sortOrder}) as unknown as GridCellCertificateElement )
    json.sort_order = sortOrder
    gridCell.initialize(this.rootModel, this, certificateElementType.GridCell, json);
    gridCell.initializeAfterParentSet();
    this.initializeEmbedded(this, gridCell, sortOrder,  json, false );
    return gridCell
  }

  clearRepeatableInputs() {
    this.allInternalElements.forEach(element => element.clearRepeatableInputs())
  }

  get allInternalElements() {
    const elements = [];
    for (const cell of this.certificateElementsArray) {
      elements.push(cell);
      const cellInternalElement = cell.certificateElementsArray[0];
      if (cellInternalElement) elements.push(cellInternalElement);
    }
    return elements;
  }
}